/**
 * The products this console covers, and which of them the platform can reach.
 *
 * The console is organised PER PRODUCT because the system is: a merchant holds
 * a tier in Labeeb and a tier in MCP, and those are two rows in two stores that
 * can disagree. The design draws that split everywhere — a "Stored as" cell
 * reading `labeeb:standard / mcp:premium`, a `tier split` badge when they
 * diverge — and it is the reason a per-product breakdown is not decoration.
 * Collapsing the two into one "Tier" column would hide the one fact an operator
 * most needs: that the merchant is paying for Pro and getting Plus on one side.
 *
 * TWO DIFFERENT ABSENCES, and conflating them would mislead:
 *
 *   `declared: false`  The platform has no route table for this product at all.
 *                      `infrastructure/infra/products.py` maps a product name
 *                      to the routes the gateway mounts, and a product with no
 *                      entry there has nothing to call in ANY environment, no
 *                      matter the config or the deploy. This is a roadmap
 *                      fact — true of neither product today, since both now
 *                      have an entry, but the distinction stays because a
 *                      third product will start here again.
 *
 *   `declared: true`,  Declared, but this environment mounted no routes,
 *   health 404s        because the product's `lambda_arn` SSM parameter is
 *                      absent. MCP in prod is exactly this today. An
 *                      operational fact about one gateway, and it can change
 *                      without a line of code here.
 *
 * The health probe answers the second. Only this file answers the first, which
 * is why `declared` is not derived from a fetch.
 *
 * KEEP IN STEP with `PRODUCT_ROUTES` in infrastructure/infra/products.py. That
 * is the authority — the gateway enumerates every route rather than proxying a
 * catch-all, so a product missing from it is unreachable no matter what this
 * file says. A cross-language check would be more machinery than the drift
 * deserves; `declared` is one boolean and the flag surface names the file.
 */

export type ProductId = "mcp" | "labeeb";

export interface ProductDef {
  /** Matches the gateway prefix AND the key in products.py. */
  id: ProductId;
  label: string;
  /** Path prefix on the gateway: `${apiPrefix}/admin/...`. */
  apiPrefix: string;
  /** One line on what this product is, for the flag surface. */
  blurb: string;
  /**
   * Does `PRODUCT_ROUTES` in products.py have an entry? False means no route
   * exists to call, so nothing in this console may fetch from it — see the
   * docstring above for why this is not the same as an unreachable gateway.
   */
  declared: boolean;
  /**
   * What it would take to wire it, precisely enough to act on. Rendered
   * verbatim wherever the product's half of a surface is missing, so an
   * operator meeting a blank cell learns why rather than filing a bug.
   *
   * Undefined for a declared product — its absence, if any, is environmental
   * and the health probe explains it.
   */
  needs?: string;
}

export const PRODUCTS: ProductDef[] = [
  {
    id: "labeeb",
    label: "Labeeb",
    apiPrefix: "/labeeb",
    blurb: "The merchant-facing assistant. Owns tiers and quotas, standing, settings and rules.",
    // Listed FIRST because the design puts it first everywhere — `labeeb:`
    // above `mcp:` in every stored-as cell — and because it owns the tier a
    // merchant actually buys. products.py now mounts /labeeb/* (dev) and
    // client.ts unwraps its {detail: {code, message}} error shape, so the
    // roadmap fact this flag used to encode is no longer true.
    declared: true,
  },
  {
    id: "mcp",
    label: "MCP",
    apiPrefix: "/mcp",
    blurb: "The tool server. Owns per-tool daily caps, the blocklist and runtime settings.",
    declared: true,
  },
];

export function productById(id: ProductId): ProductDef | undefined {
  return PRODUCTS.find((p) => p.id === id);
}

/** The products this console may actually call. Nothing else may be fetched. */
export const DECLARED_PRODUCTS: ProductDef[] = PRODUCTS.filter((p) => p.declared);

/** The products drawn but not reachable — what the flag surface enumerates. */
export const UNWIRED_PRODUCTS: ProductDef[] = PRODUCTS.filter((p) => !p.declared);
